<template>
  <div class="tracking-wrapper">
    <loading v-if="isLoading" />

    <div
      v-else-if="error"
      class="d-flex flex-column align-items-center justify-content-center vh-100 bg-light"
    >
      <div class="text-center p-4">
        <h2 class="text-danger mb-3">Invalid Tracking Link</h2>
        <p class="text-muted">
          The provided tracking token is invalid, malformed, or has expired.
        </p>
      </div>
    </div>

    <div v-else id="token-map" style="width: 100%; height: 100vh;"></div>
  </div>
</template>

<script>
import { decryptPayload } from "../utils/decryptToken";
import { base64Icons } from "../components/map/Tracking/base64Icons";
import { createMapClientForPoints } from "../maps";

export default {
  name: "TokenTracking",
  data() {
    return {
      isLoading: true,
      error: false,
      locations: null,
      map: null,
    };
  },
  async mounted() {
    const token = this.$route.query.token;
    if (!token) {
      this.handleError();
      return;
    }

    const secretKey = process.env.VUE_APP_TOKEN_TRACKING_KEY;
    if (!secretKey) {
      console.error("VUE_APP_TOKEN_TRACKING_KEY is not set");
      this.handleError();
      return;
    }

    try {
      const payload = decryptPayload(token, secretKey);

      const hasPickups =
        Array.isArray(payload?.pickupLocations) &&
        payload.pickupLocations.length > 0;
      const hasDeliveries =
        Array.isArray(payload?.deliveryLocations) &&
        payload.deliveryLocations.length > 0;

      if (!hasPickups && !hasDeliveries) {
        throw new Error("No locations in payload");
      }

      this.locations = payload;

      // Render the #token-map div before initializing the map
      this.isLoading = false;
      await this.$nextTick();
      await this.initMap();
    } catch (err) {
      console.error("Tracking init failed:", err);
      this.handleError();
    }
  },
  methods: {
    handleError() {
      this.error = true;
      this.isLoading = false;
    },
    async initMap() {
      const valid = (loc) =>
        typeof loc.latitude === "number" && typeof loc.longitude === "number";
      const toPos = (loc) => ({ lat: loc.latitude, lng: loc.longitude });

      const pickups = (this.locations.pickupLocations || []).filter(valid);
      const deliveries = (this.locations.deliveryLocations || []).filter(valid);
      const points = [...pickups, ...deliveries].map(toPos);

      // Hudhud covers Saudi only; out-of-KSA points fall back to Google.
      const client = await createMapClientForPoints(points);
      this.map = client.createMap("token-map");

      pickups.forEach((loc) =>
        this.map.addIconMarker({
          position: toPos(loc),
          iconUrl: base64Icons.PICK_UP,
          size: 40,
        })
      );
      deliveries.forEach((loc) =>
        this.map.addIconMarker({
          position: toPos(loc),
          iconUrl: base64Icons.DELIVERY,
          size: 40,
        })
      );

      this.map.fitBounds(points, { maxZoom: 16 });
    },
  },
};
</script>

<style scoped>
.tracking-wrapper {
  position: relative;
  width: 100%;
  height: 100vh;
}
</style>
